import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  getMembershipIntervalDisplaySingle,
  Membership,
  MembershipLocationSpecificPricingMatrix,
  MembershipPerCustomerAddOn,
  MembershipUpfrontPaymentOption
} from '../../../../../../../models/membership';
import { MembershipDiscountType, MembershipPurchaseType } from '../../../../../../../models/membership-enums';
import { TaxRate } from '../../../../../../../models/tax-rate';
import { CapitanLocation } from '../../../../../../../models/location';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { dateStringFromISO8601, deleteProp } from '../../../../../../../utils';
import { ensureGroupTierDefaults, usesSpecificAllthenticateProducts } from '../../../../../../../utils-models';
import { SelectLocationModalComponent } from '../../../../modals/select-location-modal/select-location-modal.component';
import {
  EditMembershipPerCustomerAddOnModalComponent
} from '../modals/edit-membership-per-customer-add-on-modal/edit-membership-per-customer-add-on-modal.component';
import {
  EditMembershipPerLocationPricingMatrixModalComponent
} from '../modals/edit-membership-per-location-pricing-matrix-modal/edit-membership-per-location-pricing-matrix-modal.component';
import {
  AddMembershipUpfrontPaymentOptionModalComponent
} from '../modals/add-membership-upfront-payment-option-modal/add-membership-upfront-payment-option-modal.component';

/**
 * The Pricing section shared by the Membership page and the Roster Membership
 * page (PRO-1549). Edits `membership` in place and never touches host state;
 * the inputs and outputs below are the whole contract, and the host owns
 * tax-rate loading, the per-location tax-rate flags and the unsaved-changes flag.
 */
@Component({
  selector: 'app-membership-pricing-section',
  templateUrl: './membership-pricing-section.component.html'
})
export class MembershipPricingSectionComponent {

  @Input() membership: Membership;
  @Input() errors: any = {};
  @Input() taxRates: TaxRate[];
  @Input() loadingTaxRates = false;
  @Input() locations: CapitanLocation[] | null;
  @Input() isExistingNonDraftMembership = false;
  @Input() hasPerLocationTaxRates = false;
  @Output() hasPerLocationTaxRatesChange = new EventEmitter<boolean>();
  @Input() hasPerLocationInitiationFeeTaxRates = false;
  @Output() hasPerLocationInitiationFeeTaxRatesChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<void>();
  @Output() refreshTaxRates = new EventEmitter<void>();

  public readonly MembershipPurchaseType = MembershipPurchaseType;
  public readonly MembershipDiscountType = MembershipDiscountType;
  public readonly Object = Object;
  public readonly deleteProp = deleteProp;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly getMembershipIntervalDisplaySingle = getMembershipIntervalDisplaySingle;
  public readonly ensureGroupTierDefaults = ensureGroupTierDefaults;

  constructor(
    public orgConfig: OrganizationConfigService,
    private modalService: NgbModal,
  ) {}

  public getDiscountedPrice(basePrice: number, discountAmount: number) {
    if (this.membership.discount_type === MembershipDiscountType.currency) {
      return basePrice - discountAmount;
    } else if (this.membership.discount_type === MembershipDiscountType.percentage) {
      return basePrice * (1 - (discountAmount / 100.0));
    } else {
      return 0;
    }
  }

  public addAdultDiscount() {
    const numDiscounts = this.membership.adult_discounts.length;
    this.membership.adult_discounts.push({
      number_in_tier: numDiscounts + 1,
      discount_amount: this.membership.adult_discounts[numDiscounts - 1].discount_amount
    });
  }

  public addYouthDiscount() {
    const numDiscounts = this.membership.youth_discounts.length;
    this.membership.youth_discounts.push({
      number_in_tier: numDiscounts + 1,
      discount_amount: this.membership.youth_discounts[numDiscounts - 1].discount_amount
    });
  }

  public getPricingMatrixAdultNums() {
    let minNumAdults: number;
    if (this.membership.has_youth_tier) {
      minNumAdults = this.membership.minimum_num_adults;
      if (minNumAdults < 0) {
        minNumAdults = 0;
      }
    } else {
      minNumAdults = this.membership.minimum_num_members;
      if (minNumAdults < 1) {
        minNumAdults = 1;
      }
    }

    let maxNumAdults = this.membership.adult_discounts.length;
    if (this.membership.allow_unlimited_adults) {
      maxNumAdults++;
    }

    return Array(
      maxNumAdults - minNumAdults + 1
    ).fill(0).map((x, i) => i + minNumAdults);
  }

  public getPricingMatrixYouthNums() {
    let minNumYouths = this.membership.minimum_num_youths;
    if (minNumYouths < 0) {
      minNumYouths = 0;
    }

    let maxNumYouths = this.membership.youth_discounts.length;
    if (this.membership.allow_unlimited_youths) {
      maxNumYouths++;
    }

    // Start at 0 youths
    return Array(
      maxNumYouths - minNumYouths + 1
    ).fill(0).map((x, i) => i + minNumYouths);
  }

  public memberNumAllowed(numAdults: number, numYouth: number): boolean {
    if (this.membership.has_youth_tier) {
      if (numAdults < this.membership.minimum_num_adults) { return false; }
      if (numYouth < this.membership.minimum_num_youths) { return false; }
      const numMembers = numAdults + numYouth;
      if (numMembers < 1) { return false; }
      if (numMembers < this.membership.minimum_num_members) { return false; }
    } else {
      if (numAdults < this.membership.minimum_num_members) { return false; }
    }

    return true;
  }

  public getGroupPrice(adultNum: number, youthNum: number) {
    let price = 0;

    if (adultNum > 0) {
      if (this.membership.discounts_are_incremental) {
        for (let adultCount = 1; adultCount <= adultNum; adultCount++) {
          price += this.getFeeForAdultCount(adultCount);
        }
      } else {
        price += this.getFeeForAdultCount(adultNum) * adultNum;
      }
    }

    if (youthNum > 0) {
      if (this.membership.discounts_are_incremental) {
        for (let youthCount = 1; youthCount <= youthNum; youthCount++) {
          price += this.getFeeForYouthCount(youthCount);
        }
      } else {
        price += this.getFeeForYouthCount(youthNum) * youthNum;
      }
    }

    return price;
  }

  public editPerCustomerAddOn(addOn: MembershipPerCustomerAddOn) {
    const modalRef = this.modalService.open(EditMembershipPerCustomerAddOnModalComponent);
    modalRef.componentInstance.inputAddOn = addOn;
    modalRef.componentInstance.isGroupMembership = this.membership.is_group;
    modalRef.componentInstance.taxRateList = this.taxRates;
    modalRef.componentInstance.usesSpecificAllthenticateProducts =
      this.usesSpecificAllthenticateProducts();
    modalRef.componentInstance.completedCallback = (result: MembershipPerCustomerAddOn) => {
      this.changed.emit();
      for (const key of Object.keys(result)) {
        addOn[key] = result[key];
      }
    };
  }

  public addPerCustomerAddOn() {
    const modalRef = this.modalService.open(EditMembershipPerCustomerAddOnModalComponent);
    modalRef.componentInstance.isGroupMembership = this.membership.is_group;
    modalRef.componentInstance.taxRateList = this.taxRates;
    modalRef.componentInstance.usesSpecificAllthenticateProducts =
      this.usesSpecificAllthenticateProducts();
    modalRef.componentInstance.completedCallback = (result: MembershipPerCustomerAddOn) => {
      this.changed.emit();
      this.membership.per_customer_add_ons.push(result);
      for (const mat of this.membership.location_specific_pricing_matrices) {
        // Pricing matrices saved before add-on fees existed lack this map.
        if (!mat.per_customer_add_on_fees) { mat.per_customer_add_on_fees = {}; }
        mat.per_customer_add_on_fees[result.id] = result.fee;
      }
    };
  }

  public removePerCustomerAddOn(addOnIdx: number) {
    const isConfirmed = confirm(
      'Are you sure you want to remove this add-on? After the membership is ' +
      'saved, this add-on will be removed from all existing customer ' +
      'memberships.'
    );
    if (isConfirmed) {
      this.changed.emit();
      const addOnToRemove = this.membership.per_customer_add_ons[addOnIdx];
      this.membership.per_customer_add_ons.splice(addOnIdx, 1);
      for (const mat of this.membership.location_specific_pricing_matrices) {
        if (mat.per_customer_add_on_fees) {
          mat.per_customer_add_on_fees[addOnToRemove.id] = undefined;
        }
      }
    }
  }

  public editPricingMatrix(matIdx: number) {
    if (!this.locations) { return; }

    const modalRef = this.modalService.open(
      EditMembershipPerLocationPricingMatrixModalComponent
    );
    modalRef.componentInstance.inputPricingMatrix = this.membership.location_specific_pricing_matrices[matIdx];
    modalRef.componentInstance.membership = this.membership;
    modalRef.componentInstance.locations = this.locations;
    modalRef.componentInstance.completedCallback = (result: MembershipLocationSpecificPricingMatrix) => {
      this.changed.emit();
      this.removeLocationIdsFromExistingPricingMatrices(result.location_ids);
      this.membership.location_specific_pricing_matrices[matIdx] = result;
    };
  }

  public addPricingMatrix() {
    if (!this.locations) { return; }

    const modalRef = this.modalService.open(
      EditMembershipPerLocationPricingMatrixModalComponent
    );
    modalRef.componentInstance.membership = this.membership;
    modalRef.componentInstance.locations = this.locations;
    modalRef.componentInstance.completedCallback = (result: MembershipLocationSpecificPricingMatrix) => {
      this.changed.emit();
      this.removeLocationIdsFromExistingPricingMatrices(result.location_ids);
      this.membership.location_specific_pricing_matrices.push(result);
    };
  }

  public removePricingMatrix(matIdx: number) {
    const isConfirmed = confirm(
      'Are you sure you want to remove this per-location pricing matrix?'
    );
    if (isConfirmed) {
      this.changed.emit();
      this.membership.location_specific_pricing_matrices.splice(matIdx, 1);
    }
  }

  public addUpfrontPaymentOption() {
    const modalRef = this.modalService.open(
      AddMembershipUpfrontPaymentOptionModalComponent
    );
    modalRef.componentInstance.membership = this.membership;
    modalRef.componentInstance.completedCallback = (result: MembershipUpfrontPaymentOption) => {
      this.changed.emit();
      this.membership.upfront_payment_options.push(result);
      this.membership.upfront_payment_options.sort(
        (a, b) => {
          return a.number_of_periods - b.number_of_periods;
        }
      )
    }
  }

  public editUpfrontPaymentOption(upoIdx: number) {
    const modalRef = this.modalService.open(
      AddMembershipUpfrontPaymentOptionModalComponent
    );
    modalRef.componentInstance.membership = this.membership;
    modalRef.componentInstance.existingOption = this.membership.upfront_payment_options[upoIdx];
    modalRef.componentInstance.completedCallback = (result: MembershipUpfrontPaymentOption) => {
      this.changed.emit();
      this.membership.upfront_payment_options[upoIdx] = result;
      this.membership.upfront_payment_options.sort(
        (a, b) => {
          return a.number_of_periods - b.number_of_periods;
        }
      );
    };
  }

  public removeUpfrontPaymentOption(upoIdx: number) {
    const isConfirmed = confirm(
      'Are you sure you want to remove this upfront payment option?'
    );
    if (isConfirmed) {
      this.changed.emit();
      this.membership.upfront_payment_options.splice(upoIdx, 1);
    }
  }

  public taxRateRemoved(taxRateId: number) {
    if (!this.taxRates) { return true; }

    for (const taxRate of this.taxRates) {
      if (taxRate.id === taxRateId) { return false; }
    }

    return true;
  }

  public getTaxRateNameForId(taxRateId: number) {
    if (!this.taxRates) { return ''; }

    for (const taxRate of this.taxRates) {
      if (taxRate.id === taxRateId) {
        return taxRate.name;
      }
    }
    return '';
  }

  public addTaxRateLocation() {
    if (this.loadingTaxRates) {
      return;
    }

    const modalRef = this.modalService.open(SelectLocationModalComponent);
    modalRef.componentInstance.excludeIds = Object
      .keys(this.membership.tax_rate_per_location)
      .map(locId => parseInt(locId, 10));
    modalRef.componentInstance.submitCallback = (result: CapitanLocation) => {
      this.membership.tax_rate_per_location[result.id.toString()] = this.taxRates[0].id;
      this.changed.emit();
    }
  }

  public addTaxRateInitiationFeeLocation() {
    if (this.loadingTaxRates) {
      return;
    }

    const modalRef = this.modalService.open(SelectLocationModalComponent);
    modalRef.componentInstance.excludeIds = Object
      .keys(this.membership.tax_rate_initiation_fee_per_location)
      .map(locId => parseInt(locId, 10));
    modalRef.componentInstance.submitCallback = (result: CapitanLocation) => {
      this.membership.tax_rate_initiation_fee_per_location[result.id.toString()] = this.taxRates[0].id;
      this.changed.emit();
    }
  }

  public getLocNameById(locId: string | number) {
    if (!this.locations) {
      return 'Loading...';
    }

    for (const loc of this.locations) {
      if (loc.id.toString() === locId.toString()) { return loc.name; }
    }

    return 'Unknown Location';
  }

  /**
   * Return the add-on's fee for the given pricing policy, or null if the
   * add-on is not available at that policy's locations.
   */
  public getPricingMatrixAddOnFee(
    pricingMatrix: MembershipLocationSpecificPricingMatrix,
    addOn: MembershipPerCustomerAddOn
  ): string | null {
    if (!pricingMatrix.per_customer_add_on_fees) { return null; }

    const fee = pricingMatrix.per_customer_add_on_fees[addOn.id];
    if (fee === undefined || fee === null || fee === '') { return null; }

    return fee;
  }

  private usesSpecificAllthenticateProducts(): boolean {
    return usesSpecificAllthenticateProducts(
      this.orgConfig.getOrganizationConfig()
    );
  }

  private getFeeForAdultCount(adultCount: number) {
    let adultDiscount: number;
    if (adultCount <= this.membership.adult_discounts.length) {
      adultDiscount = this.membership.adult_discounts[adultCount - 1].discount_amount;
    } else {
      const numDis = this.membership.adult_discounts.length;
      adultDiscount = this.membership.adult_discounts[numDis - 1].discount_amount;
    }
    return this.getDiscountedPrice(this.membership.adult_base_fee, adultDiscount);
  }

  private getFeeForYouthCount(youthCount: number) {
    let youthDiscount: number;
    if (youthCount <= this.membership.youth_discounts.length) {
      youthDiscount = this.membership.youth_discounts[youthCount - 1].discount_amount;
    } else {
      const numDis = this.membership.youth_discounts.length;
      youthDiscount = this.membership.youth_discounts[numDis - 1].discount_amount;
    }
    return this.getDiscountedPrice(this.membership.youth_base_fee, youthDiscount);
  }

  private removeLocationIdsFromExistingPricingMatrices(locIds: number[]) {
    for (const thisMatrix of this.membership.location_specific_pricing_matrices) {
      thisMatrix.location_ids = thisMatrix.location_ids
        .filter(locId => !locIds.includes(locId));
    }
  }
}
